import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, CheckCircle2, PackageSearch, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  getUnmetDemand,
  updateUnmetDemandStatus,
  type UnmetDemandItem,
  type UnmetDemandStatus,
} from '@/services/dashboardApi'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore } from '@/store/authStore'

const CONVERSATION_ROUTE = (conversationId: string) => `/app/conversations/${conversationId}`

function formatDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('es-VE', { day: 'numeric', month: 'short' })
}

export interface UnmetDemandViewProps {
  items: UnmetDemandItem[]
  pendingId: string | null
  onResolve: (id: string, status: Exclude<UnmetDemandStatus, 'open'>) => void
}

/** Lista de lo que pidieron los clientes y no hay en el inventario. Solo presentación. */
export function UnmetDemandView({ items, pendingId, onResolve }: UnmetDemandViewProps) {
  if (items.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-600">
        Por ahora todo lo que te pidieron estaba en tu inventario. Si un cliente pide algo que no tienes, aparecerá aquí.
      </p>
    )
  }

  return (
    <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
      {items.map((item) => {
        const lastRequest = item.recentRequests[0]
        const busy = pendingId === item.id
        return (
          <li key={item.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{item.label}</p>
              <p className="mt-0.5 text-xs text-slate-600">
                {item.requestCount === 1 ? 'Lo pidió 1 cliente' : `Lo pidieron ${item.requestCount} clientes`} · último pedido{' '}
                {formatDate(item.lastRequestedAt)}
              </p>
              {item.lastCustomerMessage && (
                <p className="mt-1 truncate text-xs italic text-slate-500">«{item.lastCustomerMessage}»</p>
              )}
              {lastRequest && (
                <Link
                  to={CONVERSATION_ROUTE(lastRequest.conversationId)}
                  className="mt-1 inline-block text-xs font-semibold text-brand underline"
                >
                  Ver el chat
                </Link>
              )}
            </div>
            <div className="flex flex-shrink-0 gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => onResolve(item.id, 'stocked')}
                aria-label={`Marcar ${item.label} como ya disponible`}
              >
                <CheckCircle2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Ya lo tengo
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => onResolve(item.id, 'dismissed')}
                aria-label={`Descartar ${item.label}`}
              >
                <X className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Descartar
              </Button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export default function UnmetDemandWidget() {
  const tenantId = useAuthStore((state) => state.tenantId)
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: queryKeys.unmetDemand(tenantId),
    queryFn: getUnmetDemand,
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  })

  const mutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Exclude<UnmetDemandStatus, 'open'> }) => updateUnmetDemandStatus(id, status),
    onSuccess: (_data, variables) => {
      toast.success(variables.status === 'stocked' ? 'Marcado como disponible' : 'Pedido descartado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.unmetDemand(tenantId) })
    },
    onError: () => toast.error('No se pudo actualizar. Inténtalo de nuevo.'),
  })

  return (
    <section aria-labelledby="unmet-demand-title" className="space-y-3">
      <div className="flex items-center gap-2">
        <PackageSearch className="h-5 w-5 text-brand" aria-hidden="true" />
        <h2 id="unmet-demand-title" className="text-base font-bold text-slate-900">
          Lo que te pidieron y no tienes
        </h2>
      </div>
      <p className="text-xs text-slate-600">
        El asistente le dijo al cliente que no estaba disponible, sin ofrecerle otra cosa. Úsalo para decidir qué traer.
      </p>

      {query.isPending ? (
        <div className="space-y-2" aria-busy="true" aria-label="Cargando pedidos sin inventario">
          <Skeleton height={64} className="w-full" />
          <Skeleton height={64} className="w-full" />
        </div>
      ) : query.isError ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-rose-800">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            No pudimos cargar estos pedidos.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      ) : (
        <UnmetDemandView
          items={query.data ?? []}
          pendingId={mutation.isPending ? (mutation.variables?.id ?? null) : null}
          onResolve={(id, status) => mutation.mutate({ id, status })}
        />
      )}
    </section>
  )
}
